import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FolderOpen, 
  FileText, 
  Github, 
  ExternalLink, 
  User, 
  Trash2 
} from 'lucide-react';
import TLDRPopup from './TLDRPopup';

const DesktopContainer = styled.div`
  width: 100vw;
  height: 100vh;
  position: relative;
  background: transparent;
  cursor: default;
  padding-top: 24px; /* Account for menu bar */
`;

const WelcomeText = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 1;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 90vw;
  max-height: 90vh;
`;

const WelcomeLine1 = styled(motion.div)`
  font-size: 24px;
  color: #666;
  font-weight: 300;
  margin-bottom: 8px;
  pointer-events: auto;
`;

const WelcomeLine2 = styled(motion.div)`
  font-size: 48px;
  color: #333;
  font-weight: 600;
  font-family: 'Georgia', serif;
  pointer-events: auto;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: perspective(1000px) rotateX(10deg) rotateY(5deg) scale(1.05);
    text-shadow: 
      2px 2px 0px rgba(0,0,0,0.1),
      4px 4px 0px rgba(0,0,0,0.05);
  }
`;

const DesktopIconsContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const DesktopIcon = styled(motion.div)`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

const IconImage = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  position: relative;
  color: ${props => props.color || '#333'};
`;

const IconLabel = styled.div`
  font-size: 12px;
  color: #333;
  text-align: center;
  max-width: 80px;
  word-wrap: break-word;
  line-height: 1.2;
`;

const Desktop = ({ onDesktopClick, onOpenTrash, onOpenAboutMe }) => {
  const [iconPositions, setIconPositions] = useState({
    resume: { x: 50, y: 100 },
    aboutMe: { x: 50, y: 250 },
    project1: { x: window.innerWidth - 200, y: 100 },
    project2: { x: window.innerWidth - 200, y: 250 },
    project3: { x: window.innerWidth - 200, y: 400 },
    project4: { x: window.innerWidth - 200, y: 550 },
    project5: { x: window.innerWidth - 200, y: 700 },
    trash: { x: window.innerWidth - 200, y: window.innerHeight - 200 }
  });

  const projects = [
    { 
      name: "Atithi - Travel Platform", 
      icon: <FolderOpen size={48} />, 
      color: "#FFD700", 
      link: "https://github.com/floofy244/atithi", 
      type: "github", 
      key: "project1",
      demo: "https://github.com/floofy244/atithi",
      summary: "A scalable full-stack travel platform built with React/Node.js. Features JWT authentication, AWS S3 media handling, and real-time booking system. Reduced API latency by 25% in stress tests."
    },
    { 
      name: "CommuniTea Chat App", 
      icon: <FolderOpen size={48} />, 
      color: "#FF6B6B", 
      link: "https://github.com/floofy244/Online_Chat_App", 
      type: "github", 
      key: "project2",
      demo: "https://communiteachat.onrender.com/",
      summary: "Real-time chat application built with Django Channels and WebSockets. Handles 50+ concurrent users with <250ms message delivery latency. Features user registration and profile management."
    },
    { 
      name: "Speech-to-Text App", 
      icon: <FolderOpen size={48} />, 
      color: "#4ECDC4", 
      link: "https://github.com/floofy244/speech-to-text", 
      type: "github", 
      key: "project3",
      demo: "https://github.com/floofy244/speech-to-text",
      summary: "Advanced speech recognition application using Python, Django, React, and Faster Whisper. Provides real-time transcription with high accuracy and supports multiple languages."
    }
  ];
  
  const handleIconClick = (project) => {
    // Open demo link in new tab
    if (project.demo) {
      window.open(project.demo, '_blank');
    }
  };

  const handleTrashClick = (e) => {
    e.stopPropagation(); // Prevent desktop click from triggering
    if (onOpenTrash) {
      onOpenTrash();
    }
  };

  const handleAboutMeClick = (e) => {
    e.stopPropagation(); // Prevent desktop click from triggering
    if (onOpenAboutMe) {
      onOpenAboutMe();
    }
  };

  // Grid snapping function
  const snapToGrid = (x, y) => {
    const gridSize = 20; // Grid size in pixels
    return {
      x: Math.round(x / gridSize) * gridSize,
      y: Math.round(y / gridSize) * gridSize
    };
  };

  const handleIconDragEnd = (key, event, info) => {
    const newX = iconPositions[key].x + info.offset.x;
    const newY = iconPositions[key].y + info.offset.y;
    const snapped = snapToGrid(newX, newY);
    
    setIconPositions(prev => ({
      ...prev,
      [key]: snapped
    }));
  };

  return (
    <DesktopContainer onClick={onDesktopClick}>
      <WelcomeText
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
      >
        <WelcomeLine1
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          welcome to my
        </WelcomeLine1>
        <WelcomeLine2
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          whileHover={{
            scale: 1.05,
            rotateX: 10,
            rotateY: 5,
            transition: { duration: 0.3 }
          }}
        >
          portfolio.
        </WelcomeLine2>
      </WelcomeText>

      <DesktopIconsContainer>
        {/* Resume PDF */}
        <DesktopIcon
          drag
          dragMomentum={false}
          dragElastic={0}
          onDragEnd={(e, info) => handleIconDragEnd('resume', e, info)}
          onClick={(e) => {
            e.stopPropagation();
            window.open('/resume.pdf', '_blank');
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          style={{ 
            left: iconPositions.resume.x, 
            top: iconPositions.resume.y 
          }}
        >
          <IconImage color="#FF3B30">
            <FileText size={48} />
          </IconImage>
          <IconLabel>Resume.pdf</IconLabel>
        </DesktopIcon>

        {/* About Me */}
        <DesktopIcon
          drag
          dragMomentum={false}
          dragElastic={0}
          onDragEnd={(e, info) => handleIconDragEnd('aboutMe', e, info)}
          onClick={handleAboutMeClick}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3 }}
          style={{ 
            left: iconPositions.aboutMe.x, 
            top: iconPositions.aboutMe.y 
          }}
        >
          <IconImage color="#007AFF">
            <FolderOpen size={48} />
          </IconImage>
          <IconLabel>About Me</IconLabel>
        </DesktopIcon>

        {/* Project Icons */}
        {projects.map((project, index) => (
          <DesktopIcon
            key={project.key}
            drag
            dragMomentum={false}
            dragElastic={0}
            onDragEnd={(e, info) => handleIconDragEnd(project.key, e, info)}
            onClick={(e) => {
              e.stopPropagation();
              handleIconClick(project);
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 + index * 0.1 }}
            style={{ 
              left: iconPositions[project.key].x, 
              top: iconPositions[project.key].y 
            }}
          >
            <IconImage color={project.color}>
              {project.icon}
              {project.type === 'github' && (<Github size={16} style={{ position: 'absolute', bottom: 0, right: 0, color: '#333' }} />)}
              {project.type === 'external' && (<ExternalLink size={16} style={{ position: 'absolute', bottom: 0, right: 0, color: '#007AFF' }} />)}
            </IconImage>
            <IconLabel>{project.name}</IconLabel>
          </DesktopIcon>
        ))}

        {/* Trash - positioned at bottom right like macOS */}
        <DesktopIcon
          drag
          dragMomentum={false}
          dragElastic={0}
          onDragEnd={(e, info) => handleIconDragEnd('trash', e, info)}
          onClick={handleTrashClick}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          style={{ 
            left: iconPositions.trash.x, 
            top: iconPositions.trash.y 
          }}
        >
          <IconImage color="#666">
            <Trash2 size={48} />
          </IconImage>
          <IconLabel>Don't Look</IconLabel>
        </DesktopIcon>
      </DesktopIconsContainer>
    </DesktopContainer>
  );
};

export default Desktop;
